import { useMutation } from '@tanstack/react-query'
import { useFormikContext } from 'formik'
import { useState } from 'react'

import type { AxiosError } from 'axios'
import type { ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { IVersionEngineFormValues, ENGINE_TYPE } from '~/types/arbitration'

import { checkEligibility } from '~/api/arbitration'
import Banner from '~/components/Banner'
import Button from '~/components/Buttons/Button'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import CreatableSelectInput from '~/components/Inputs/Basic/CreatableSelectInput'
import InputWrapper from '~/components/Inputs/Wrapper'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { handleGenericError } from '~/utils/error'
import { reactSelectMenuPositionDef } from '~/utils/options'

import './styles.scss'

const MODAL_ID = 'add-versions-modal'

const getWarningMessage = (versions: string[]): string => {
  if (versions.length > 1) {
    return `Some versions (${versions.join(', ')}) already exist in other group(s)`
  }
  return `Version ${versions[0]} already exists in another group`
}

const getCheckboxMessage = (versions: string[]): string => {
  if (versions.length > 1) {
    return `By checking this box, you will remove them from their group`
  }
  return `By checking this box, you will remove it from its group`
}

const versionInGroupErrorMessage = 'Some version(s) already belong to this group. Please remove them to continue'

interface FormProps {
  appKey: string
  deviceType: string
  engineType: ENGINE_TYPE.VERSION | ENGINE_TYPE.ARCHIVE
  onClose: VoidFunction
  onSave: (values: { appVersions: string[]; eligibilityError?: Record<string, string>; force: boolean }) => void
}

const AddVersionsModalForm = ({ appKey, deviceType, engineType, onClose, onSave }: FormProps): ReactElement => {
  const {
    values: { versions },
  } = useFormikContext<IVersionEngineFormValues>()

  const [appVersions, setAppVersions] = useState<IOption[]>([])

  const [versionInGroupError, setVersionInGroupError] = useState<string>()

  const [eligibilityError, setEligibilityError] = useState<Record<string, string>>()

  const [isEligibilityErrorChecked, setIsEligibilityErrorChecked] = useState(false)

  const { isPending: isMutationPending, mutateAsync: checkEligibilityMutation } = useMutation({
    mutationFn: checkEligibility,
  })

  const handleSelectChange = async (options: IOption[]) => {
    setAppVersions(options)

    if (options.filter(({ value }) => versions?.includes(value)).length > 0) {
      setVersionInGroupError(versionInGroupErrorMessage)
    } else {
      setVersionInGroupError(undefined)
      if (options.length === 0) {
        setEligibilityError(undefined)
      } else {
        try {
          await checkEligibilityMutation({
            appKey,
            appVersions: options.map(({ value }) => value),
            deviceType,
            engineType,
          })
          setEligibilityError(undefined)
        } catch (e) {
          const error = e as AxiosError<IApiError>

          if (error.response?.data.code === 409) {
            const error = (e as AxiosError<IApiError>).response?.data.message

            error && setEligibilityError(JSON.parse(error))
          } else {
            handleGenericError(e)
          }
        }
      }
    }
  }

  const handleEligibilityErrorCheckboxChange = () => setIsEligibilityErrorChecked((checked) => !checked)

  const handleSave = () => {
    onSave({
      appVersions: appVersions.map(({ value }) => value),
      eligibilityError,
      force: !!eligibilityError && isEligibilityErrorChecked,
    })
  }

  const isSaveButtonDisabled =
    (!!eligibilityError && !isEligibilityErrorChecked) ||
    !!versionInGroupError ||
    appVersions.length === 0 ||
    isMutationPending

  return (
    <>
      <ModalBody>
        <div className="add-versions-modal-content">
          <div className="add-versions-modal-content__input">
            <InputWrapper className="select-input" error={versionInGroupError}>
              <CreatableSelectInput
                {...reactSelectMenuPositionDef(MODAL_ID)}
                isMulti
                label="App versions"
                onChange={(options) => handleSelectChange(options as IOption[])}
                placeholder="Ex: 1.1.0"
                value={appVersions}
              />
            </InputWrapper>
          </div>
          {eligibilityError ? (
            <>
              <div className="add-versions-modal-content__warning">
                <Banner variant="warning">{getWarningMessage(Object.keys(eligibilityError))}</Banner>
              </div>
              <div className="add-versions-modal-content__checkbox">
                <CheckboxInput
                  checked={isEligibilityErrorChecked}
                  label={getCheckboxMessage(Object.keys(eligibilityError))}
                  onChange={handleEligibilityErrorCheckboxChange}
                />
              </div>
            </>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose} type="button">
          Cancel
        </Button>
        <Button disabled={isSaveButtonDisabled} onClick={handleSave} type="button">
          Save
        </Button>
      </ModalFooter>
    </>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
  title: string
}

const AddVersionsModal = ({ isOpen, onClose, title, ...props }: ModalProps): ReactElement => {
  return (
    <Modal id={MODAL_ID} isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{title}</ModalHeader>
      <AddVersionsModalForm {...props} onClose={onClose} />
    </Modal>
  )
}

export default AddVersionsModal
